/**
 * 节流函数 - 限制函数执行频率
 * @param func 需要节流的函数
 * @param wait 等待时间（毫秒）
 * @param options 可选配置项
 * @returns 节流后的函数
 */
export function throttle<T extends (...args: any[]) => any>(
    func: T,
    wait: number,
    options: {
        leading?: boolean; // 是否在开始时立即执行
        trailing?: boolean; // 是否在结束时再执行一次
    } = {}
): (...args: Parameters<T>) => ReturnType<T> { // 1. 修改返回值类型
    let timeout: number | null = null;
    let previous = 0;
    let result: ReturnType<T>; // 2. 增加变量，用于存储函数执行结果
    const { leading = true, trailing = true } = options;

    return function (this: any, ...args: Parameters<T>): ReturnType<T> { // 3. 修改返回函数的返回值类型
        const now = Date.now();

        // 如果是第一次调用且不需要立即执行
        if (!previous && leading === false) {
            previous = now;
        }

        const remaining = wait - (now - previous);

        // 如果已经到了时间间隔或者第一次调用且需要立即执行
        if (remaining <= 0 || remaining > wait) {
            if (timeout) {
                clearTimeout(timeout);
                timeout = null;
            }
            previous = now;
            result = func.apply(this, args); // 4. 保存函数执行结果
        } else if (!timeout && trailing) {
            // 如果需要在结束时执行且没有设置定时器
            timeout = window.setTimeout(() => {
                previous = leading ? Date.now() : 0;
                timeout = null;
                result = func.apply(this, args); // 5. 在 trailing 调用时也保存结果
            }, remaining);
        }
        return result; // 6. 每次都返回最近一次的执行结果
    };
}